// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, Component, ElementRef, OnInit, ViewChild, Input, ViewContainerRef, Type, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { DocumentsService } from '@services/documents.service';
import { ErrorService } from '@services/error.service';
import { DdjDocument, DocumentStatus, IntellicheckIdUpload, IntellicheckPayload } from '@interfaces/document.interfaces';
import { catchError, filter, of, takeWhile } from 'rxjs';
import { IntellicheckUploadComponentBase } from './intellicheck-upload-component-base';
import { isExtendedId, isIdentification, isInternational_Id_Dl, isPassport, isPassportCard, isRemoteDriversLicense } from 'src/app/utils/document-utils';
import { IntellicheckLicenseFrontComponent } from './license-front/intellicheck-license-front.component';
import { IntellicheckLicenseSelfieComponent } from './intellicheck-license-selfie/intellicheck-license-selfie.component';
import { Location } from '@angular/common';
import { DynamsoftScanComponent } from './dynamsoft-scan/dynamsoft-scan.component';
import { FeatureFlagService } from '@services/feature-flag.service';
import { MatSelectionList } from '@angular/material/list';
import { IntellicheckLicenseBackComponent } from './license-back/intellicheck-license-back.component';
import { IDScanResult, IDScanningSessionLog, RequestedScanType, getRequestedScanTypeNames } from './id-scan-logging';
import { getRequestedScanTypes } from './id-scan-logging';
import { IntellicheckPassportBookletComponent } from './passport-booklet/intellicheck-passport-booklet.component';
import { RpcApiError } from '@interfaces/api.interfaces';
import './dynamsoft-config';
import { isMobileOrTablet } from 'src/app/utils/utils';

@Component({
    selector: 'intellicheck-upload',
    templateUrl: './intellicheck-upload.component.html',
    styleUrls: ['./intellicheck-upload.component.scss'],
})
export class IntellicheckUploadComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('backUploadPreview') backPreviewCanvas: ElementRef<HTMLCanvasElement>;

    @ViewChild('frontUploadPreview') frontPreviewCanvas: ElementRef<HTMLCanvasElement>;

    @ViewChild('componentContainer', { read: ViewContainerRef })
    componentContainerRef: ViewContainerRef;

    @Input()
    tokenData: { encryptedToken: string; document: DdjDocument; userId: number };

    @Input()
    showBackButton = false;

    public isLoading = false;
    public loadingMessage: string;
    public isProcessingDl = false;
    private isComponentAlive = true;
    public showPreviewUpload = false;
    private dealId: number;
    private document: DdjDocument;
    private componentDefaultsOverride: any = null;
    public loadingMessageOverrides: string[] = [];
    private extendedLoadingMessageThreshold = 4000000; //Show users a different message if upload is over 4mb so they don't get all worked up.

    public workflowComponents: Type<IntellicheckUploadComponentBase>[] = [];
    public documentIsComplete = false;

    //The data that is populated by workflow components on their captureComplete.emit
    protected allImages: Partial<IntellicheckPayload> = {};
    protected lastImageData: Partial<IntellicheckPayload> = {};

    public developmentModeAvailable = false;
    public developmentModeOn = false;
    public developmentModeComponents: { name: string; component: Type<IntellicheckUploadComponentBase> }[] = [
        { name: 'Dynamsoft Barcode Scanner', component: DynamsoftScanComponent },
        { name: 'License Front (Photo Only - Old)', component: IntellicheckLicenseFrontComponent },
        { name: 'Licence Back Scanner (Intellicheck - Old)', component: IntellicheckLicenseBackComponent },
        { name: 'Selfie', component: IntellicheckLicenseSelfieComponent },
    ];

    private idScanSessionLog: IDScanningSessionLog;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly documentsService: DocumentsService,
        private readonly errorService: ErrorService,
        private readonly location: Location,
        private readonly featureFlagService: FeatureFlagService
    ) {
        this.developmentModeAvailable = this.featureFlagService.getDefaultFeatureConfiguration('licenseCaptureDiagnosticMode');
    }

    ngOnInit() {
        this.isLoading = true;
        this.dealId = parseInt(this.route.snapshot.params.dealId);
        this.document = this.tokenData ? this.tokenData.document : this.documentsService.getDocById(parseInt(this.route.snapshot.params.docId));

        if (!isIdentification(this.document.internalDocumentTypeId)) {
            this.location.back();
        } else if (this.document.documentStatusId === DocumentStatus.Complete) {
            this.documentIsComplete = true;
            this.isLoading = false;
        } else if (!isMobileOrTablet()) {
            this.showMobileOnlyError();
        }
    }

    async ngAfterViewInit(): Promise<void> {
        if (this.documentIsComplete) {
            return;
        }

        await this.checkCameraPermissions();

        this.loadingMessage = null;

        this.idScanSessionLog = {
            requestIdentifier: this.generateLoggingGuid(),
            documentId: this.document.documentId,
            dealId: this.dealId,
            dealerId: this.document.dealerId,
            startTime: new Date(),
            scanAttempts: [],
            uploadAttempted: false,
            allScansAttempted: false,
            allScansSuccessful: false,
        };

        if (!this.developmentModeOn) {
            this.defineComponentWorkflow();
            this.loadWorkflowComponent(0);
        } else {
            this.isLoading = false;
        }

        this.subscribeToNavigation();
    }

    private defineComponentWorkflow() {
        if (isExtendedId(this.document.internalDocumentTypeId)) {
            if (isPassport(this.document.internalDocumentTypeId)) {
                this.idScanSessionLog.requestedScanTypes = RequestedScanType.PassportBooklet;
                this.workflowComponents.push(...[IntellicheckPassportBookletComponent]);
            } else if (isInternational_Id_Dl(this.document.internalDocumentTypeId) || isPassportCard(this.document.internalDocumentTypeId)) {
                this.idScanSessionLog.requestedScanTypes = RequestedScanType.Back | RequestedScanType.Front;
                this.workflowComponents.push(...[IntellicheckLicenseBackComponent, IntellicheckLicenseFrontComponent]);

                const title = isPassportCard(this.document.internalDocumentTypeId) ? 'Passport Card' : 'ID';
                this.componentDefaultsOverride = {
                    active: true,
                    frontTitle: `${title} Front Side`,
                    frontMessage: '',
                    backTitle: `${title} Back Side`,
                    backMessage: '',
                };
            } else {
                throw new Error(`Document type not supported for ID Scanning (type: ${this.document.internalDocumentTypeId}, id: ${this.document.documentId})`);
            }
        } else {
            this.idScanSessionLog.requestedScanTypes = RequestedScanType.Back | RequestedScanType.Front;
            this.workflowComponents.push(...[IntellicheckLicenseFrontComponent, DynamsoftScanComponent]);
            if (isRemoteDriversLicense(this.document.internalDocumentTypeId)) {
                this.workflowComponents.push(IntellicheckLicenseSelfieComponent);
                this.idScanSessionLog.requestedScanTypes |= RequestedScanType.Selfie;
            }
        }
        this.idScanSessionLog.requestedScanTypeNames = getRequestedScanTypeNames(this.idScanSessionLog.requestedScanTypes);
    }

    public startDevelopmentModeWorkflow(selection: MatSelectionList) {
        const comps = selection.selectedOptions?.selected?.map((s) => s.value.component as Type<IntellicheckUploadComponentBase>);
        this.workflowComponents.push(...comps);
        this.isLoading = true;
        this.loadWorkflowComponent(0);
    }

    private loadWorkflowComponent(index: number) {
        this.componentContainerRef.clear();
        if (index < this.workflowComponents.length) {
            const newComponent = this.workflowComponents[index];
            const componentRef = this.componentContainerRef.createComponent<IntellicheckUploadComponentBase>(newComponent);

            if (this.componentDefaultsOverride !== null) {
                componentRef.instance.pageDefaultOverride = this.componentDefaultsOverride;
            }

            componentRef.instance.doneLoading.subscribe(() => {
                this.isLoading = false;
            });
            componentRef.instance.partialLoading.subscribe(() => {
                this.isLoading = false;
            });
            componentRef.instance.showLoading.subscribe((message) => {
                this.isLoading = true;
                this.loadingMessage = message;
            });
            componentRef.instance.captureComplete.subscribe((data) => {
                this.loadingMessage = null;
                this.isLoading = true;
                this.allImages = { ...this.allImages, ...data };
                this.lastImageData = data;

                const isLastComponent = index + 1 === this.workflowComponents.length;
                if (!isLastComponent) {
                    // if this is not the last, upload data to be stored in s3
                    // otherwise, wait for the user to click the upload button to upload the last image.
                    this.uploadLicense(data);
                }
                this.loadWorkflowComponent(index + 1);
            });
            componentRef.instance.returnToJacket.subscribe(() => {
                this.returnToJacket();
            });
            componentRef.instance.logScanAtttempt.subscribe((log) => {
                //Need these properties to quickly address steps user is stuck on.
                log.dealId = this.dealId;
                log.docId = this.document.documentId;
                log.dealerId = this.document.dealerId;
                this.idScanSessionLog.scanAttempts.push(log);
                if (log.result === IDScanResult.BrowserBack) {
                    this.uploadScanningSessionLog();
                }
            });
            componentRef.instance.showBackButton = this.showBackButton;
            componentRef.instance.dealId = this.dealId;
            componentRef.instance.documentId = this.document.documentId;
            componentRef.instance.developmentModeOn = this.developmentModeOn;
        } else {
            this.showPreview();
        }
    }

    public onDevelopmentModeToggle() {
        this.developmentModeOn = !this.developmentModeOn;
        this.componentContainerRef.clear();
        this.workflowComponents = [];
        if (!this.developmentModeOn) {
            this.defineComponentWorkflow();
            this.loadWorkflowComponent(0);
        }
    }

    public resetDevelopmentModeWorkflow() {
        this.componentContainerRef.clear();
        this.workflowComponents = [];
    }

    showPreview() {
        const displayPreview = (canvas: HTMLCanvasElement, imageSrc: string) => {
            if (!imageSrc) return;
            const image = new Image();
            const context = canvas.getContext('2d');
            image.src = `data:image/png;base64,${imageSrc}`;
            image.onload = () => {
                canvas.classList.remove('hidden');
                canvas.width = image.width;
                canvas.height = image.height;
                canvas.style.maxWidth = image.width.toString() + 'px';
                context.drawImage(image, 0, 0, image.width, image.height);
            };
        };

        displayPreview(this.backPreviewCanvas.nativeElement, this.allImages.backData);
        displayPreview(this.frontPreviewCanvas.nativeElement, this.allImages.frontData);

        this.isLoading = false;
        this.showPreviewUpload = true;
    }

    public get uploadButtonDisplayName(): string {
        if (isExtendedId(this.document.internalDocumentTypeId)) {
            if (isPassport(this.document.internalDocumentTypeId)) {
                return 'Upload Passport Booklet';
            } else if (isPassportCard(this.document.internalDocumentTypeId)) {
                return 'Upload Passport Card';
            } else if (isInternational_Id_Dl(this.document.internalDocumentTypeId)) {
                return 'Upload International Id / International License';
            }
            throw new Error(`Document type not supported for ID Scanning (type: ${this.document.internalDocumentTypeId}, id: ${this.document.documentId})`);
        } else {
            return 'Upload License';
        }
    }

    uploadLicense(data: Partial<IntellicheckPayload>, redirectAfterUpload: boolean = false) {
        this.uploadScanningSessionLog();
        const body: IntellicheckIdUpload = {
            documentId: this.document.documentId,
            requestIdentifier: this.idScanSessionLog.requestIdentifier,
            token: this.tokenData ? decodeURIComponent(this.tokenData.encryptedToken) : null,
            payload: {
                frontData: data.frontData,
                backData: data.backData,
                barcodeData: data.barcodeData,
                selfieImage: data.selfieImage,
                requestingFacialRecognition: isRemoteDriversLicense(this.document.internalDocumentTypeId),
            },
        };
        //override the final loading message in case the images uploaded are over the threshold mb
        if (this.extendedLoadingMessageNeeded(body.payload)) {
            this.loadingMessageOverrides = [
                null,
                null,
                null,
                null,
                'Completing Upload and Validation (this may take up to 15 seconds due to large image sizes)',
            ];
        }
        this.isProcessingDl = true;
        const uploadComplete = (document) => {
            this.isProcessingDl = false;
            const isCustomError = this.checkForCustomError(document);
            if (redirectAfterUpload && !isCustomError && document && document.documentId) {
                this.returnToJacket();
            }
        };
        if (this.tokenData) {
            this.documentsService
                .intellicheckTokenUpload$(body)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((err) => {
                        this.isProcessingDl = false;
                        return of(err);
                    })
                )
                .subscribe(uploadComplete);
        } else {
            this.documentsService
                .intellicheckUpload$(this.dealId, this.document.documentId, body)
                .pipe(
                    takeWhile(() => this.isComponentAlive),
                    catchError((err) => {
                        this.isProcessingDl = false;
                        return of(err);
                    })
                )
                .subscribe(uploadComplete);
        }
    }

    uploadScanningSessionLog = async () => {
        if (this.idScanSessionLog?.uploadAttempted) return;
        try {
            if (!this.idScanSessionLog || !(this.idScanSessionLog.scanAttempts?.length > 0)) return;
            this.idScanSessionLog.uploadTime = new Date();
            this.idScanSessionLog.uploadAttempted = true;
            const token = this.tokenData ? decodeURIComponent(this.tokenData.encryptedToken) : null;
            if (token) this.idScanSessionLog.token = token;
            this.idScanSessionLog.allScansAttempted = this.allScanTypesAttempted();
            this.idScanSessionLog.allScansSuccessful = this.allScanTypesSuccessful();
            await this.documentsService.uploadIdScanningSessionLog$(this.idScanSessionLog).subscribe();
        } catch (e) {
            //Do nothing, if logging fails we don't need to raise an error or alert the user
        }
    };

    checkForCustomError(response): boolean {
        if (response.error && response.statusCode == 550) {
            this.errorService.showError({
                message: response.message,
                title: response.name,
                statusCode: 550,
            });
            return true;
        }
        return false;
    }

    extendedLoadingMessageNeeded(payload: IntellicheckPayload): boolean {
        let totalUploadSize = 0;
        if (payload.backData && payload.barcodeData) {
            totalUploadSize += payload.barcodeData.length;
        } else if (!payload.barcodeData && payload.backData) {
            totalUploadSize += payload.backData.length;
        }
        if (payload.frontData) {
            totalUploadSize += payload.frontData.length;
        }
        if (payload.selfieImage) {
            totalUploadSize += payload.selfieImage.length;
        }
        //Show extended loading message if > 3mb
        //currently we are doing full front size image and barcode if it works, this will be less than 3mb usually
        //but if barcode fails over to full size image we will be above this and should add a message about it taking longer due to size
        return totalUploadSize >= this.extendedLoadingMessageThreshold;
    }

    cancelUpload() {
        this.returnToJacket();
    }

    returnToJacket() {
        this.uploadScanningSessionLog();
        if (this.tokenData) {
            this.router.navigate(['/upload', this.dealId], {
                queryParams: {
                    token: this.tokenData.encryptedToken,
                },
            });
        } else {
            const routeToGo = window.location.href.includes('/main/vehicles')
                ? ['/main', 'vehicles', this.dealId]
                : window.location.href.includes('/dashboard/deals')
                  ? ['/dashboard', 'deals', this.dealId]
                  : window.location.href.includes('/dashboard/documents')
                    ? ['/dashboard', 'documents', this.dealId]
                    : null;

            this.router.navigate(routeToGo);
        }
    }

    async checkCameraPermissions() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            stream.getTracks().forEach((t) => t.stop());
        } catch (err) {
            let userError: RpcApiError;
            switch (err.name) {
                case 'NotAllowedError':
                case 'NotFoundError':
                    userError = {
                        title: 'Camera Permission Required',
                        message:
                            "This site requires access to your device's camera. For Android, click on icon in your browser bar to open settings and enable camera permission. " +
                            'For iOS, click on your phone settings, then open your browser settings and enable camera permission.',
                    };
                    break;
                case 'NotReadableError':
                case 'AbortError':
                    userError = {
                        title: 'Camera In Use',
                        message:
                            "Your device's camera is currently in use by another application. Close any applications using your camera. If this site is still using your camera, please refresh this page.",
                    };
                    break;
                default:
                    userError = {
                        title: 'Camera Permission Error',
                        message: 'An error occurred while trying to obtain camera permissions.  Please refresh this site and try again.',
                    };
            }
            const errorModal = this.errorService.showErrorAtService(userError);
            errorModal
                .afterClosed()
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe(() => this.returnToJacket());
        }
    }

    showMobileOnlyError() {
        const error = {
            title: 'Mobile Only Upload',
            message: 'This document can only be uploaded on a mobile device for security reasons.',
        };
        const errorModal = this.errorService.showErrorAtService(error);
        errorModal
            .afterClosed()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe(() => this.location.back());
    }

    allScanTypesAttempted = (): boolean => {
        if (!this.idScanSessionLog || !(this.idScanSessionLog.scanAttempts?.length > 0)) return false;
        const requestedScanTypes = getRequestedScanTypes(this.idScanSessionLog.requestedScanTypes);
        const attemptedScanTypes = this.idScanSessionLog.scanAttempts.map((a) => a.type);
        return requestedScanTypes.every((t) => attemptedScanTypes.includes(t));
    };

    allScanTypesSuccessful = (): boolean => {
        if (!this.idScanSessionLog || !(this.idScanSessionLog.scanAttempts?.length > 0)) return false;
        const requestedScanTypes = getRequestedScanTypes(this.idScanSessionLog.requestedScanTypes);
        const successfulScanTypes = this.idScanSessionLog.scanAttempts.filter((a) => a.result === IDScanResult.Success).map((a) => a.type);
        return requestedScanTypes.every((t) => successfulScanTypes.includes(t));
    };

    private generateLoggingGuid = (): string => {
        if ('crypto' in window && 'randomUUID' in window['crypto']) {
            return window['crypto'].randomUUID();
        } else return Math.round(Math.random() * Math.pow(10, 10)).toString();
    };

    subscribeToNavigation = () => {
        this.router.events
            .pipe(takeWhile(() => this.isComponentAlive))
            .pipe(filter((event) => event instanceof NavigationStart))
            .subscribe((event: NavigationStart) => {
                //Since this is the end of the routing path, we can assume that the user is leaving the page if the navigationTrigger is popstate
                if (event.navigationTrigger === 'popstate') {
                    if (this.showPreviewUpload) {
                        this.uploadScanningSessionLog();
                    }
                }
            });
    };

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
